'use client';

import React, { useEffect } from 'react';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { SectionCard } from '@/components/ui/section-card';

export const getWeskiPartnerId = () => {
  const locale = getLocale();
  switch (locale) {
    case ELocale.EN_US:
    case ELocale.EN_GB:
    case ELocale.CS_CZ:
    case ELocale.SK_SK:
      return '3e08';
    case ELocale.FR_FR:
      return 'yc8q';
    default:
      // for all other locales
      return 'p7s9';
  }
};

const IS_FR = getLocale() === ELocale.FR_FR;

const WeSkiWidget = ({ destinationId, withTopCopy }: { destinationId: string, withTopCopy?: boolean }) => {
  const { t } = useOTSTranslation('common');

  useEffect(() => {
    // Monkeypatch Element.prototype.attachShadow to detect when the WeSki widget
    // initialises its shadow DOM. WeSki uses a closed shadow root (mode: 'closed'),
    // so element.shadowRoot is always null from the outside - we cannot poll for it.
    // Intercepting the native method is the only reliable way to know the widget
    // has mounted. When running inside the mobile app's WebView, we notify the
    // native side so it can reveal the previously hidden WebView container.
    const originalAttachShadow = Element.prototype.attachShadow;
    Element.prototype.attachShadow = function attachShadow(init) {
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(JSON.stringify({ event: 'weski_loaded' }));
      }
      return originalAttachShadow.call(this, init);
    };

    // Guard against the script being added more than once. React StrictMode and
    // Fast Refresh both unmount/remount components, which would otherwise append
    // duplicate <script> tags and cause WeSki to call attachShadow twice on the
    // same element, throwing NotSupportedError. We intentionally do NOT remove
    // the script in the cleanup - once loaded, global JS state (custom elements,
    // observers) cannot be safely unregistered, so re-executing the script is
    // guaranteed to crash.
    let script = document.querySelector('script[src="https://widget.weski.com"]') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://widget.weski.com';
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Restore the original attachShadow so unrelated code on other pages
      // is not affected after this component unmounts.
      Element.prototype.attachShadow = originalAttachShadow;
    };
  }, []);

  return (
    <SectionCard as="article" padding="none" className="overflow-hidden">
      {withTopCopy
        && (
          <p className="p-4">
            {t('resort.bookTrip.copy')}
          </p>
        )}
      <div
        id="weski-widget"
        data-destination-id={destinationId}
        data-locale={IS_FR ? 'fr' : undefined}
        data-partner={getWeskiPartnerId()}
        data-new-tab="true"
      />
    </SectionCard>
  );
};

export default WeSkiWidget;
